import { cn } from '@/lib/cn'
import type { FieldOptions } from '@/api/types'
import { Badge } from '@/components/ui/Badge'
import { Select } from '@/components/ui/Field'
import { Button } from '@/components/ui/Button'
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  LayoutGrid,
  Rows3,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

export interface FilterState {
  q: string
  cuisine: string[]
  protein_base: string[]
  tags: string[]
  vegetarian: 'all' | 'yes' | 'no'
  excluded: 'all' | 'yes' | 'no'
  min_rating: number | null
  max_effort: number | null
  not_prepped_within_days: number | null
  sort: string
  order: 'asc' | 'desc'
  view: 'grid' | 'table'
}

/**
 * Multi-value filter. Laid out like Field so it lines up with the neighbouring
 * selects: caption above, control below, same height.
 */
function MultiSelect({
  label,
  values,
  options,
  onChange,
}: {
  label: string
  values: string[]
  options: string[]
  onChange: (next: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const available = options.filter((option) => !values.includes(option))

  function toggle(value: string) {
    onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value])
  }

  return (
    <div className="relative flex min-w-0 flex-col gap-1.5">
      <span className="text-label font-medium text-ink-soft">{label}</span>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-[var(--radius-field)] border px-3 text-sm',
          values.length > 0
            ? 'border-accent bg-accent-soft text-accent-ink'
            : 'border-line-strong bg-surface text-ink hover:bg-surface-sunken',
        )}
      >
        <span className="truncate">
          {values.length === 0
            ? 'Any'
            : values.length === 1
              ? values[0]
              : `${values.length} selected`}
        </span>
        {values.length > 0 ? (
          <span
            role="button"
            tabIndex={0}
            aria-label={`Clear ${label}`}
            onClick={(event) => {
              event.stopPropagation()
              onChange([])
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.stopPropagation()
                onChange([])
              }
            }}
            className="rounded p-0.5 hover:bg-accent/15"
          >
            <X size={13} />
          </span>
        ) : (
          <ChevronDown size={14} aria-hidden className="shrink-0 text-ink-faint" />
        )}
      </button>
      {open ? (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="scroll-slim absolute left-0 top-full z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-[var(--radius-field)] border border-line bg-surface p-1 shadow-raised">
            {available.length === 0 ? (
              <p className="px-2 py-3 text-center text-xs text-ink-faint">
                All options selected
              </p>
            ) : (
              available.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggle(option)}
                  className="flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm text-ink hover:bg-surface-sunken"
                >
                  {option}
                </button>
              ))
            )}
          </div>
        </>
      ) : null}
    </div>
  )
}

export function FilterBar({
  state,
  options,
  total,
  onChange,
  sortOptions,
}: {
  state: FilterState
  options: FieldOptions | undefined
  total: number
  onChange: (next: Partial<FilterState>) => void
  sortOptions: Array<{ value: string; label: string }>
}) {
  // Filters hide behind a toggle so the list leads with the recipes. The panel
  // starts open when a filter is already active, so arriving on a bookmarked or
  // back-navigated filtered URL never looks like it silently ignored its own
  // query parameters. After that the toggle owns the state.
  const [open, setOpen] = useState(() => hasActiveFilters(state))
  const container = useRef<HTMLDivElement>(null)

  // A click outside the panel closes it. Bound on the container rather than the
  // panel so the toggle button itself is inside the ref.
  useEffect(() => {
    if (!open) return
    const onClick = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const filterCount = countActiveFilters(state) - (state.q ? 1 : 0)

  function reset() {
    onChange({
      q: '',
      cuisine: [],
      protein_base: [],
      tags: [],
      vegetarian: 'all',
      excluded: 'all',
      min_rating: null,
      max_effort: null,
      not_prepped_within_days: null,
    })
  }

  return (
    <div ref={container} className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <CollapsibleSearch value={state.q} onChange={(q) => onChange({ q })} />

        {/* Only once a search is in play: the syntax is dead weight otherwise. */}
        {state.q ? (
          <p className="w-full text-micro text-ink-faint">
            <code className="text-ink-soft">ingredient:&lt;name&gt;</code> finds recipes
            containing that ingredient. Combine with words or a second term to narrow
            further.
          </p>
        ) : null}

        <Button
          variant={filterCount > 0 ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          aria-controls="recipe-filters"
        >
          <SlidersHorizontal size={14} aria-hidden />
          Filters
          {filterCount > 0 ? (
            <span className="tabular ml-0.5 rounded-full bg-accent px-1.5 py-px text-micro text-on-accent">
              {filterCount}
            </span>
          ) : null}
        </Button>

        <Badge tone="neutral">
          {total} {total === 1 ? 'recipe' : 'recipes'}
        </Badge>

        {filterCount > 0 ? (
          <Button variant="ghost" size="sm" onClick={reset}>
            <X size={13} aria-hidden />
            Clear {filterCount} {filterCount === 1 ? 'filter' : 'filters'}
          </Button>
        ) : null}

        <div className="ml-auto flex items-center gap-2">
          {/* Sort stays out of the panel: it is a persistent preference rather
              than a narrowing filter, and this list is mostly read by sorting. */}
          <label className="flex items-center gap-1.5">
            <span className="sr-only">Sort by</span>
            <select
              value={state.sort}
              onChange={(event) => onChange({ sort: event.target.value })}
              className="h-8 rounded-[var(--radius-field)] border border-line-strong bg-surface pl-2 text-xs text-ink hover:bg-surface-sunken"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => onChange({ order: state.order === 'asc' ? 'desc' : 'asc' })}
              title={state.order === 'asc' ? 'Ascending' : 'Descending'}
              className="inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius-field)] border border-line-strong bg-surface text-ink-soft hover:bg-surface-sunken"
            >
              {state.order === 'asc' ? (
                <ArrowUp size={14} aria-hidden />
              ) : (
                <ArrowDown size={14} aria-hidden />
              )}
              <span className="sr-only">
                {state.order === 'asc' ? 'Ascending, switch to descending' : 'Descending, switch to ascending'}
              </span>
            </button>
          </label>

          <div className="flex items-center gap-0.5 rounded-[var(--radius-field)] border border-line bg-surface p-0.5">
            {(
              [
                ['grid', 'Grid', LayoutGrid],
                ['table', 'Table', Rows3],
              ] as const
            ).map(([mode, label, Icon]) => (
              <button
                key={mode}
                type="button"
                onClick={() => onChange({ view: mode })}
                aria-pressed={state.view === mode}
                title={`${label} view`}
                className={cn(
                  'inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors',
                  state.view === mode
                    ? 'bg-accent-soft text-accent-ink'
                    : 'text-ink-faint hover:bg-surface-sunken hover:text-ink',
                )}
              >
                <Icon size={13} aria-hidden />
                <span className="sr-only sm:not-sr-only">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {open ? (
        <div
          id="recipe-filters"
          className="space-y-3 rounded-[var(--radius-card)] border border-line bg-surface p-4"
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <MultiSelect
              label="Cuisine"
              values={state.cuisine}
              options={options?.cuisines ?? []}
              onChange={(cuisine) => onChange({ cuisine })}
            />
            <MultiSelect
              label="Protein"
              values={state.protein_base}
              options={options?.protein_bases ?? []}
              onChange={(protein_base) => onChange({ protein_base })}
            />
            <MultiSelect
              label="Tags"
              values={state.tags}
              options={options?.tags ?? []}
              onChange={(tags) => onChange({ tags })}
            />
            <Select
              label="Vegetarian"
              value={state.vegetarian}
              onChange={(event) =>
                onChange({ vegetarian: event.target.value as FilterState['vegetarian'] })
              }
              options={[
                { value: 'all', label: 'All' },
                { value: 'yes', label: 'Only vegetarian' },
                { value: 'no', label: 'Not vegetarian' },
              ]}
            />
            <Select
              label="Excluded"
              value={state.excluded}
              onChange={(event) =>
                onChange({ excluded: event.target.value as FilterState['excluded'] })
              }
              options={[
                { value: 'all', label: 'All' },
                { value: 'no', label: 'Active only' },
                { value: 'yes', label: 'Excluded only' },
              ]}
            />
            <Select
              label="Minimum rating"
              value={state.min_rating ?? ''}
              onChange={(event) =>
                onChange({
                  min_rating: event.target.value ? Number(event.target.value) : null,
                })
              }
              placeholder="Any"
              options={[
                { value: '3', label: '3+' },
                { value: '4', label: '4+' },
                { value: '5', label: '5 only' },
              ]}
            />
            <Select
              label="Effort up to"
              value={state.max_effort ?? ''}
              onChange={(event) =>
                onChange({ max_effort: event.target.value ? Number(event.target.value) : null })
              }
              placeholder="Any"
              options={[
                { value: '1', label: 'Low' },
                { value: '2', label: 'Medium' },
                { value: '3', label: 'High' },
              ]}
            />
            <Select
              label="Not made in"
              value={state.not_prepped_within_days ?? ''}
              onChange={(event) =>
                onChange({
                  not_prepped_within_days: event.target.value
                    ? Number(event.target.value)
                    : null,
                })
              }
              placeholder="Any time"
              options={[
                { value: '30', label: 'Last 30 days' },
                { value: '60', label: 'Last 2 months' },
                { value: '90', label: 'Last 3 months' },
                { value: '180', label: 'Last 6 months' },
              ]}
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}

function countActiveFilters(state: FilterState): number {
  return (
    (state.q ? 1 : 0) +
    state.cuisine.length +
    state.protein_base.length +
    state.tags.length +
    (state.vegetarian !== 'all' ? 1 : 0) +
    (state.excluded !== 'all' ? 1 : 0) +
    (state.min_rating ? 1 : 0) +
    (state.max_effort ? 1 : 0) +
    (state.not_prepped_within_days ? 1 : 0)
  )
}

function hasActiveFilters(state: FilterState): boolean {
  return countActiveFilters(state) > (state.q ? 1 : 0)
}

/**
 * Search collapses to its icon until it is needed. An active term keeps it
 * expanded, so a filtered list never hides the reason it is filtered.
 */
function CollapsibleSearch({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  // An active term keeps the field open, so a filtered list never hides the
  // reason it is filtered behind an icon.
  const [open, setOpen] = useState(value !== '')
  const input = useRef<HTMLInputElement>(null)

  function expand() {
    setOpen(true)
    // The input is not mounted until after this state lands.
    requestAnimationFrame(() => input.current?.focus())
  }

  if (!open) {
    return (
      <Button variant="ghost" size="sm" onClick={expand} title="Search recipes">
        <Search size={14} aria-hidden />
        <span className="sr-only">Search recipes</span>
      </Button>
    )
  }

  return (
    <div className="relative min-w-0 flex-1 sm:max-w-xs">
      <Search
        size={15}
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-ink-faint"
      />
      <input
        ref={input}
        type="search"
        value={value}
        placeholder="Name, or ingredient:…"
        aria-label="Search recipes by name, or by ingredient"
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            if (value) onChange('')
            else setOpen(false)
          }
        }}
        className="h-8 w-full rounded-[var(--radius-field)] border border-line-strong bg-surface pr-8 pl-8 text-sm text-ink placeholder:text-ink-faint/70 hover:border-ink-faint focus:border-accent"
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="absolute top-1/2 right-1.5 -translate-y-1/2 rounded p-1 text-ink-faint hover:text-ink"
        >
          <X size={13} />
        </button>
      ) : null}
    </div>
  )
}

/** Search field with a leading icon, used where the label would be redundant. */
export function SearchField({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
}) {
  return (
    <div className="relative flex min-w-0 flex-col gap-1.5">
      <span className="text-label font-medium text-ink-soft">Search</span>
      <div className="relative">
        <Search
          size={15}
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-faint"
        />
        <input
          type="search"
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          className="h-10 w-full rounded-[var(--radius-field)] border border-line-strong bg-surface pr-3 pl-9 text-sm text-ink placeholder:text-ink-faint/70 hover:border-ink-faint focus:border-accent"
        />
      </div>
    </div>
  )
}
